import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { validateForm, emailSchema, nameSchema, passwordSchema, phoneSchema } from '../../utils/validation';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Label } from '../../components/Label';
import { initiateGoogleSso } from '../../utils/sso';

const localRegisterSchema = z.object({
  name: nameSchema,
  tenantName: z.string().min(1, 'Company name is required'),
  email: emailSchema,
  mobileNumber: phoneSchema,
  password: passwordSchema,
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine((value) => value, 'You must accept the terms'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type FormData = z.infer<typeof localRegisterSchema>;

function PasswordField({
  id,
  label,
  value,
  onChange,
  visible,
  onToggle,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  visible: boolean;
  onToggle: () => void;
  error?: string;
}) {
  const errorId = `${id}-error`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          autoComplete={id === 'password' ? 'new-password' : 'new-password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className="pr-10"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {error && <p id={errorId} className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}

export function Register() {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError, pendingRequest, clearPendingRequest } = useAuthStore();
  const [formData, setFormData] = useState<FormData>({
    name: '', tenantName: '', email: '', mobileNumber: '', password: '', confirmPassword: '', acceptTerms: false,
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const update = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setValidationErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    if (error) clearError();
    if (pendingRequest) clearPendingRequest();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearError();
    const validation = validateForm(localRegisterSchema, formData);
    if (!validation.success) {
      setValidationErrors(validation.errors || {});
      return;
    }
    try {
      const res = await register({ name: formData.name, email: formData.email, password: formData.password, tenantName: formData.tenantName });
      if (res?.status === 'pending_approval') {
        return;
      }
      toast.success('Registration successful! Welcome to TeamOne.');
      navigate('/dashboard');
    } catch {
      // The auth store exposes the server error to this view.
    }
  };

  const strength = [
    formData.password.length >= 8,
    /[A-Z]/.test(formData.password),
    /[a-z]/.test(formData.password),
    /[0-9]/.test(formData.password),
    /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  ].filter(Boolean).length;
  const strengthLabel = strength <= 1 ? 'Weak' : strength === 2 ? 'Fair' : strength === 3 ? 'Good' : strength === 4 ? 'Strong' : 'Excellent';
  const strengthTone = strength <= 1 ? 'bg-destructive' : strength < 4 ? 'bg-warning' : 'bg-success';

  if (pendingRequest) {
    return (
      <section className="rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8 space-y-6">
        <header className="text-center space-y-2">
          <div className="size-12 rounded-full bg-warning/10 text-warning flex items-center justify-center mx-auto border border-warning/20">
            <span className="text-xl font-semibold">!</span>
          </div>
          <h1 className="font-display text-page-title">Access Request Submitted</h1>
          <p className="text-xs font-mono text-muted-foreground">{pendingRequest.email}</p>
        </header>

        <div className="rounded-lg border border-border bg-secondary/40 p-4 text-xs text-muted-foreground space-y-2 leading-relaxed">
          <p className="font-medium text-foreground">Awaiting Administrator Approval</p>
          <p>{pendingRequest.message}</p>
          <p className="text-xs pt-1 text-muted-foreground border-t border-border/40">
            An administrator will review your account, assign your department, role, and initial task, and activate your access.
          </p>
        </div>

        <Button
          type="button"
          fullWidth
          variant="primary"
          onClick={() => {
            clearPendingRequest();
            navigate('/login');
          }}
        >
          Return to Sign In
        </Button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8">
      <header className="mb-6">
        <h1 className="font-display text-page-title">Create account</h1>
        <p className="mt-1.5 text-sm text-text-tertiary">Set up your TeamOne workspace.</p>
      </header>

      {error && <div role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="name" label="Full name" value={formData.name} onChange={(value) => update('name', value)} error={validationErrors.name} autoComplete="name" />
          <Field id="tenantName" label="Company name" value={formData.tenantName} onChange={(value) => update('tenantName', value)} error={validationErrors.tenantName} autoComplete="organization" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="email" label="Email address" type="email" value={formData.email} onChange={(value) => update('email', value)} error={validationErrors.email} autoComplete="email" />
          <Field id="mobileNumber" label="Mobile number" type="tel" value={formData.mobileNumber} onChange={(value) => update('mobileNumber', value)} error={validationErrors.mobileNumber} autoComplete="tel" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <PasswordField id="password" label="Password" value={formData.password} onChange={(value) => update('password', value)} visible={showPassword} onToggle={() => setShowPassword((value) => !value)} error={validationErrors.password} />
          <PasswordField id="confirmPassword" label="Confirm password" value={formData.confirmPassword} onChange={(value) => update('confirmPassword', value)} visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((value) => !value)} error={validationErrors.confirmPassword} />
        </div>

        {formData.password && (
          <div role="status" aria-live="polite" className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground"><span>Password strength</span><span>{strengthLabel}</span></div>
            <div className="flex h-1.5 gap-1" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((level) => <span key={level} className={`flex-1 rounded-sm ${level <= strength ? strengthTone : 'bg-muted'}`} />)}
            </div>
          </div>
        )}

        <div>
          <label htmlFor="acceptTerms" className="flex items-start gap-2 text-xs text-muted-foreground">
            <input id="acceptTerms" name="acceptTerms" type="checkbox" checked={formData.acceptTerms} onChange={(event) => update('acceptTerms', event.target.checked)} className="mt-0.5 size-4 rounded-sm border-input text-primary focus:ring-ring" />
            <span>I accept the <a href="#terms" className="text-primary hover:underline">Terms of Service</a> and <a href="#privacy" className="text-primary hover:underline">Privacy Policy</a>.</span>
          </label>
          {validationErrors.acceptTerms && <p className="mt-1 text-xs text-destructive" role="alert">{validationErrors.acceptTerms}</p>}
        </div>

        <Button type="submit" fullWidth isLoading={isLoading}>{isLoading ? 'Creating account...' : 'Create account'}</Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>

      <Button 
        type="button" 
        variant="outline" 
        fullWidth 
        onClick={() => initiateGoogleSso()}
        className="flex items-center justify-center gap-2.5 h-10 border-border bg-card hover:bg-secondary/40 text-foreground transition-all shadow-xs"
      >
        <svg className="size-4" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
          />
        </svg>
        <span className="font-medium text-sm">Sign up with Google</span>
      </Button>

      <p className="mt-6 text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-medium text-primary hover:underline">Sign in</Link></p>
    </section>
  );
}

function Field({ id, label, type = 'text', value, onChange, error, autoComplete }: { id: string; label: string; type?: string; value: string; onChange: (value: string) => void; error?: string; autoComplete?: string }) {
  const errorId = `${id}-error`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required>{label}</Label>
      <Input id={id} name={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete} invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} />
      {error && <p id={errorId} className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}
